import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CollapsibleSection, labelCls, selectCls } from './shared';
import { fetchAuthorizedFile } from '../../../api/client';
import { downloadTextFile } from '../../../utils/download';
import { usePermission } from '../../../hooks/usePermission';

type BackupStatus = 'idle' | 'loading' | 'done' | 'error';

function localDateStamp(): string {
  const p = (n: number) => String(n).padStart(2, '0');
  const d = new Date();
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export default function DataPanel() {
  const navigate = useNavigate();
  const { can } = usePermission();
  const canBackup = can('dataBackup');
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [showConfirm, setShowConfirm] = useState<'cache' | null>(null);
  const [backupStatus, setBackupStatus] = useState<BackupStatus>('idle');
  const [backupMessage, setBackupMessage] = useState('');

  async function downloadBackup() {
    // 一次备份要扫全账本，按钮在途时不给第二次机会
    if (!canBackup || backupStatus === 'loading') return;
    setBackupStatus('loading');
    setBackupMessage('');
    try {
      const { body, filename } = await fetchAuthorizedFile('/backup/export');
      if (!body.trim()) throw new Error('备份文件为空，请稍后重试');
      // 解析只为回显清单条数；落盘用的是服务端原文，解析再序列化会把列名与精度改掉。
      const counts = (JSON.parse(body) as { manifest?: { counts?: Record<string, number> } })
        ?.manifest?.counts;
      const name = filename ?? `数据备份_${localDateStamp()}.json`;
      downloadTextFile(name, body, 'application/json');
      const summary =
        counts && typeof counts.orders === 'number'
          ? `（订单 ${counts.orders} 条、客户 ${counts.customers ?? 0} 条）`
          : '';
      setBackupStatus('done');
      setBackupMessage(`已下载 ${name}${summary}`);
    } catch (e) {
      setBackupStatus('error');
      setBackupMessage(e instanceof Error && e.message ? e.message : '备份失败，请稍后重试');
    }
  }

  return (
    <div className="space-y-3 md:space-y-6">
      <CollapsibleSection title="数据导出">
        <div className="bg-gray-50 rounded-xl p-4 space-y-4">
          <p className="text-sm text-gray-600">在对应管理页按条件筛选后导出为本地文件，支持 Excel / CSV / PDF 等格式。</p>
          <div>
            <label className={labelCls}>默认导出格式</label>
            <select value={exportFormat} onChange={e => setExportFormat(e.target.value)} className={selectCls}>
              <option value="xlsx">Excel (.xlsx)</option>
              <option value="csv">CSV (.csv)</option>
              <option value="json">JSON (.json)</option>
            </select>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => navigate('/orders')}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-sm text-gray-700 rounded-lg hover:border-gray-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-download-2-line" />
              </div>
              前往订单页导出
            </button>
            <button
              type="button"
              onClick={() => navigate('/customers')}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-sm text-gray-700 rounded-lg hover:border-gray-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-3-line" />
              </div>
              前往客户页导出
            </button>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="数据备份" defaultOpen={false}>
        <div className="bg-gray-50 rounded-xl p-4 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 flex shrink-0 items-center justify-center bg-white rounded-lg">
              <i
                className={`${canBackup ? 'ri-download-cloud-2-line text-teal-500' : 'ri-lock-line text-gray-400'} text-base`}
              />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800">下载我的数据快照（JSON）</p>
              <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
                快照范围是本人账本：我创建的订单（含回收站里的）及其订单项、成本、支付记录、备注、人员调用、状态与操作日志、对账明细与结算批次，
                另含我名下的客户档案和自定义的客户类型 / 标签 / 服务类型 / 订单分类。
                口令、身份证号、各类令牌都不会写进备份文件。
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <button
              type="button"
              onClick={downloadBackup}
              disabled={!canBackup || backupStatus === 'loading'}
              title={canBackup ? '导出一份完整的 JSON 快照' : '数据备份为尊享会员专属功能'}
              className={`flex items-center justify-center gap-2 px-4 py-2.5 text-sm rounded-lg transition-colors whitespace-nowrap ${
                canBackup
                  ? 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300 cursor-pointer'
                  : 'bg-gray-100 border border-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <i className={canBackup ? 'ri-download-line' : 'ri-lock-line'} />
              {backupStatus === 'loading' ? '正在生成快照…' : '开始备份'}
            </button>
            {!canBackup && (
              <span className="text-xs text-amber-600">数据备份为尊享会员专属功能</span>
            )}
          </div>
          {backupMessage && (
            <p className={`text-xs ${backupStatus === 'error' ? 'text-red-500' : 'text-teal-600'}`}>
              {backupMessage}
            </p>
          )}
          <p className="text-xs text-gray-400 leading-relaxed">
            备份文件用于自行留存与迁移交接；平台当前只提供导出，没有一键导入入口，
            需要把数据搬回系统时请联系客服协助处理。
          </p>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="缓存与存储" defaultOpen={false}>
        <div className="flex items-center justify-between py-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-lg">
              <i className="ri-database-2-line text-gray-400 text-base" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800">清除本地缓存</p>
              <p className="text-xs text-gray-400 mt-0.5">清除浏览器本地数据（含登录态），清除后需重新登录</p>
            </div>
          </div>
          {showConfirm === 'cache' ? (
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setShowConfirm(null)} className="px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
              <button
                type="button"
                onClick={() => { localStorage.clear(); window.location.href = '/login'; }}
                className="px-3 py-1.5 text-xs text-white bg-red-400 hover:bg-red-500 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                确认清除
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowConfirm('cache')}
              className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              清除缓存
            </button>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
}
